import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Application } from 'src/app/enums/application.enums';
import Player from 'video.js/dist/types/player';
import videojs from 'video.js';
import { Subject } from 'rxjs';

const VIDEO_THRESHOLD = 0.18;
const RANGE_HEADER = 'Range';
const RANGE_HEADER_LOWER = 'range';

type VhsXhr = {
  onRequest: (callback: (options: { headers?: Record<string, string> }) => unknown) => void;
};

type VideoJsWithVhs = typeof videojs & {
  Vhs?: {
    xhr?: VhsXhr;
  };
};

@Injectable({
  providedIn: 'root'
})
export class MediaService {
  readonly currentTimeMap = new Map<string, number>();
  private playerContainerObj: Record<string, HTMLElement> = {};
  public intersectionObserver: IntersectionObserver | null = null;
  private isBrowser: boolean;
  private videoUnmutedSubject = new Subject<void>();
  public videoUnmuted$ = this.videoUnmutedSubject.asObservable();
  private musicPlayerStartedSubject = new Subject<void>();
  public musicPlayerStarted$ = this.musicPlayerStartedSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser && typeof window !== 'undefined') {
      this.createIntersectionObserver();
      this.registerVhsCorsSafeXhr();
    }
  }

  private registerVhsCorsSafeXhr() {
    const vhsXhr = (videojs as VideoJsWithVhs).Vhs?.xhr;
    if (!vhsXhr?.onRequest) {
      return;
    }
    vhsXhr.onRequest((options) => {
      if (options?.headers) {
        delete options.headers[RANGE_HEADER];
        delete options.headers[RANGE_HEADER_LOWER];
      }
      return options;
    });
  }

  private createIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((item) => {
          try {
            const player: Player = (item?.target as any)?.parentElement?.player;
            if (player) {
              item.intersectionRatio > VIDEO_THRESHOLD
                ? player.play()
                : player.pause();
            }
          } catch (_err) {}
        });
      },
      {
        threshold: VIDEO_THRESHOLD,
        rootMargin: '-20% 0px -30% 0px',
        root: typeof document !== 'undefined' ? document : null
      }
    );
  }

  readonly getUrlExtension = (url: string): string => {
    const type = url
      ?.split(/[#?]/)
      ?.[Application.ZERO]?.split('.')
      ?.pop()
      ?.trim()
      ?.toLowerCase();
    if (['png', 'webp', 'jpeg', 'jpg'].includes(type)) {
      return 'image';
    } else if (
      ['mp4', 'webm', 'heif', 'quicktime', 'mov', 'm3u8'].includes(type)
    ) {
      return 'video';
    }
    return '';
  };

  readonly isQuickTimeFormat = (file: File): boolean =>
    file?.type === 'video/quicktime';

  setIVSPlayer(
    postId: string,
    item: HTMLElement,
    autoPlay: boolean = true
  ) {
    if (!this.isBrowser) return;

    const foundCurrentTime = this.currentTimeMap.get(postId);
    if (foundCurrentTime) {
      const player = (item as any)?.firstElementChild?.player as Player;
      if (player) {
        player.currentTime(foundCurrentTime);
      }
    }

    this.playerContainerObj[postId] = item;

    if (autoPlay) {
      this.intersectionObserver?.observe(item);
    }
  }

  removePlayer(postId: string) {
    if (!this.isBrowser) return;
    const item = this.playerContainerObj[postId];
    if (item) {
      this.intersectionObserver?.unobserve(item);
      delete this.playerContainerObj[postId];
    }
  }

  pauseRestPlayers(postId: string) {
    if (!this.isBrowser) return;

    for (const key in this.playerContainerObj) {
      if (postId !== key) {
        const player: Player = (this.playerContainerObj[key] as any)
          ?.firstElementChild?.player;
        if (player) {
          player.pause();
        }
      }
    }
  }

  notifyVideoUnmuted(): void {
    this.videoUnmutedSubject.next();
  }

  notifyMusicPlayerStarted(): void {
    this.musicPlayerStartedSubject.next();
  }

  muteAllUnmutedVideos(): void {
    if (!this.isBrowser) return;

    // Mute all video players that are currently playing and unmuted
    for (const key in this.playerContainerObj) {
      const player: Player = (this.playerContainerObj[key] as any)
        ?.firstElementChild?.player;
      if (player && !player.paused() && !player.muted()) {
        player.muted(true);
      }
    }

    // Also mute any direct HTML video elements that are playing and unmuted
    const allVideoElements = document.querySelectorAll('video');
    allVideoElements.forEach((videoEl: HTMLVideoElement) => {
      if (!videoEl.paused && !videoEl.muted) {
        videoEl.muted = true;
      }
    });
  }

  pauseAllNgxAudioPlayers(): void {
    if (!this.isBrowser) return;

    // Find all ngx-audio-player components and pause their audio elements
    const audioPlayers = document.querySelectorAll('ngx-audio-player');
    audioPlayers.forEach((playerElement: Element) => {
      // Find audio element inside ngx-audio-player
      const audioElement = playerElement.querySelector('audio') as HTMLAudioElement;
      if (audioElement && !audioElement.paused) {
        audioElement.pause();
      }
    });
  }

  ngOnDestroy() {
    if (this.isBrowser) {
      this.intersectionObserver?.disconnect();
    }
  }
}
